import React, { useEffect } from "react";
import CaseStudyLayout from "../CaseStudyLayout";

const TwentyFourHourFitness = () => {
  useEffect(() => {
    document.title = "24 Hour Fitness Case Study - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  return (
    <CaseStudyLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-50 to-red-50 section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12">
            {/* Hero Image */}
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/jxcd867x_24%20Hero%20%282%29.jpg"
                alt="24 Hour Fitness Mobile App Redesign"
                className="w-full max-w-2xl mx-auto rounded-2xl shadow-xl animate-fade-in"
              />
            </div>
            
            <h1 className="heading-hero animate-slide-up text-gray-900 mb-6">
              24 Hour Fitness
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed animate-slide-up">
              Redesigned member check-in portal utilizing their established design system
            </p>
          </div>
          
          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-8 card-professional p-8 animate-scale-in">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Role</h3>
              <p className="text-gray-600">UX Designer</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Tools</h3>
              <p className="text-gray-600">Figma</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Timeline</h3>
              <p className="text-gray-600">1 Month</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Type</h3>
              <p className="text-gray-600">Redesign</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-12">
            Measurable Business Impact
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="metric-card">
              <div className="metric-number text-orange-600">45%</div>
              <div className="metric-label">Faster Check-in Process</div>
              <div className="metric-description">Reduced average completion time from 2.5 to 1.4 minutes</div>
            </div>
            <div className="metric-card">
              <div className="metric-number text-green-600">92%</div>
              <div className="metric-label">User Satisfaction Score</div>
              <div className="metric-description">Increased from 67% to 92% post-redesign</div>
            </div>
            <div className="metric-card">
              <div className="metric-number text-blue-600">60%</div>
              <div className="metric-label">Support Ticket Reduction</div>
              <div className="metric-description">Decreased from 150 to 60 tickets per week</div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow">
          <h2 className="heading-section text-center">
            The Challenge
          </h2>
          <div className="card-professional bg-white p-8 border-l-4 border-orange-500">
            <div className="flex items-start space-x-4">
              <div className="text-4xl">⚠️</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Complex User Experience Issues</h3>
                <p className="text-body">
                  The existing member check-in portal needed a comprehensive redesign to improve 
                  user experience while maintaining consistency with 24 Hour Fitness&apos;s established 
                  design system. The goal was to create a more intuitive and streamlined check-in 
                  process for gym members.
                </p>
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">Key Issues:</h4>
                    <ul className="text-red-700 space-y-1 text-sm">
                      <li>• Cluttered interface with poor hierarchy</li>
                      <li>• Inconsistent with design system</li>
                      <li>• Confusing navigation flow</li>
                      <li>• Poor mobile responsiveness</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Business Impact:</h4>
                    <ul className="text-blue-700 space-y-1 text-sm">
                      <li>• High support ticket volume</li>
                      <li>• Poor user satisfaction scores</li>
                      <li>• Reduced member engagement</li>
                      <li>• Brand inconsistency issues</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Comparison */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="heading-section text-center mb-16">
            Before & After Transformation
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Current Screen */}
            <div className="space-y-6">
              <h3 className="heading-subsection text-center text-red-600">
                Before: Current Screen
              </h3>
              <div className="card-professional p-6 bg-red-50 border border-red-200">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/l8a6mgp7_Andy%20%281%29.png"
                  alt="Current 24 Hour Fitness screen before redesign"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Issues Identified:</h4>
                <ul className="text-red-700 space-y-1 text-sm">
                  <li>• Cluttered interface with poor hierarchy</li>
                  <li>• Inconsistent with design system</li>
                  <li>• Confusing navigation flow</li>
                  <li>• Poor mobile responsiveness</li>
                </ul>
              </div>
            </div>

            {/* Redesigned Screen */}
            <div className="space-y-6">
              <h3 className="heading-subsection text-center text-green-600">
                After: Redesigned Screen
              </h3>
              <div className="card-professional p-6 bg-green-50 border border-green-200">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/8pfriy2n_grid%20template%20%281%29.png"
                  alt="Redesigned 24 Hour Fitness screen after UX improvements"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Improvements:</h4>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• Clean, organized layout</li>
                  <li>• Consistent with brand guidelines</li>
                  <li>• Intuitive user flow</li>
                  <li>• Mobile-first responsive design</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="heading-section text-center mb-16">
            Design Process & Methodology
          </h2>
          
          <div className="space-y-16">
            {/* Process Overview */}
            <div className="card-professional p-8 bg-white">
              <h3 className="heading-subsection mb-6">Research-Driven Approach</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-2xl">
                    🔍
                  </div>
                  <h4 className="font-semibold mb-2">Audit</h4>
                  <p className="text-sm text-gray-600">Analyzed current system</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-2xl">
                    👥
                  </div>
                  <h4 className="font-semibold mb-2">Research</h4>
                  <p className="text-sm text-gray-600">User interviews & testing</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl">
                    ✏️
                  </div>
                  <h4 className="font-semibold mb-2">Design</h4>
                  <p className="text-sm text-gray-600">Wireframes & prototypes</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl">
                    📊
                  </div>
                  <h4 className="font-semibold mb-2">Validate</h4>
                  <p className="text-sm text-gray-600">Testing & metrics</p>
                </div>
              </div>
            </div>

            {/* Wireframes */}
            <div className="card-professional p-8 bg-white">
              <h3 className="heading-subsection mb-8 text-center">
                Wireframes & Ideation
              </h3>
              <p className="text-body text-center mb-8 max-w-3xl mx-auto">
                I started with low-fidelity wireframes to explore different layout options and 
                user flow patterns, ensuring the redesign would address the key usability issues.
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-xl p-6">
                  <img
                    src="https://static.wixstatic.com/media/d71ae1_60959c78e0d64bf3a7522323b65b3c11~mv2.jpg/v1/fit/w_800,h_600,q_90,enc_avif,quality_auto/d71ae1_60959c78e0d64bf3a7522323b65b3c11~mv2.jpg"
                    alt="24 Hour Fitness Wireframe 1"
                    className="w-full rounded-lg"
                  />
                  <p className="text-center text-sm text-gray-600 mt-4">Initial layout exploration</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <img
                    src="https://static.wixstatic.com/media/d71ae1_373e7373422645a9b6d5599526f0979e~mv2.jpg/v1/fit/w_800,h_600,q_90,enc_avif,quality_auto/d71ae1_373e7373422645a9b6d5599526f0979e~mv2.jpg"
                    alt="24 Hour Fitness Wireframe 2"
                    className="w-full rounded-lg"
                  />
                  <p className="text-center text-sm text-gray-600 mt-4">Navigation flow refinement</p>
                </div>
              </div>
              
              {/* Additional Wireframes */}
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div className="bg-gray-50 rounded-xl p-6">
                  <img
                    src="https://static.wixstatic.com/media/d71ae1_bd48d4e57f474d05b95ebd95946a7a00~mv2.jpg/v1/fit/w_800,h_600,q_90,enc_avif,quality_auto/d71ae1_bd48d4e57f474d05b95ebd95946a7a00~mv2.jpg"
                    alt="24 Hour Fitness Wireframe 3"
                    className="w-full rounded-lg"
                  />
                  <p className="text-center text-sm text-gray-600 mt-4">Component structure</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <img
                    src="https://static.wixstatic.com/media/d71ae1_e4032f36d10340ac855c6dd5c5618a9b~mv2.jpg/v1/fit/w_800,h_600,q_90,enc_avif,quality_auto/d71ae1_e4032f36d10340ac855c6dd5c5618a9b~mv2.jpg"
                    alt="24 Hour Fitness Wireframe 4"
                    className="w-full rounded-lg"
                  />
                  <p className="text-center text-sm text-gray-600 mt-4">Final iteration</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center">
            Results & Business Impact
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="metric-card border-2 border-blue-200 bg-blue-50">
              <div className="text-4xl mb-4">⚡</div>
              <div className="metric-number text-blue-600">45%</div>
              <div className="metric-label">Faster Check-in Process</div>
              <div className="metric-description">Reduced average completion time</div>
            </div>
            <div className="metric-card border-2 border-green-200 bg-green-50">
              <div className="text-4xl mb-4">😊</div>
              <div className="metric-number text-green-600">92%</div>
              <div className="metric-label">User Satisfaction Score</div>
              <div className="metric-description">Post-implementation survey</div>
            </div>
            <div className="metric-card border-2 border-purple-200 bg-purple-50">
              <div className="text-4xl mb-4">🎯</div>
              <div className="metric-number text-purple-600">60%</div>
              <div className="metric-label">Reduction in Support Tickets</div>
              <div className="metric-description">Fewer user confusion issues</div>
            </div>
          </div>

          <div className="card-professional p-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
            <h3 className="heading-subsection text-green-800 mb-6 text-center">
              Key Takeaways & Learnings
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl mb-4">🎨</div>
                <h4 className="font-semibold text-gray-900 mb-2">Design Systems Matter</h4>
                <p className="text-gray-700 text-sm">
                  Leveraging 24 Hour Fitness&apos;s established design system ensured consistency 
                  and reduced development time.
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-4">👥</div>
                <h4 className="font-semibold text-gray-900 mb-2">User-Centered Approach</h4>
                <p className="text-gray-700 text-sm">
                  Focusing on the core user journey (check-in) simplified the interface 
                  and improved usability.
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-4">📱</div>
                <h4 className="font-semibold text-gray-900 mb-2">Mobile-First Design</h4>
                <p className="text-gray-700 text-sm">
                  Prioritizing mobile experience was crucial as most members check in 
                  using their mobile devices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container-narrow">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <button
              onClick={() => window.history.back()}
              className="interactive-link flex items-center"
              data-testid="back-to-portfolio"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Portfolio
            </button>
            
            <div className="text-sm text-gray-500">
              Case Study: 24 Hour Fitness
            </div>
          </div>
        </div>
      </section>
    </CaseStudyLayout>
  );
};

export default TwentyFourHourFitness;